import { Injectable } from '@angular/core';
import { UserProfileDto } from '@as/api';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { StorageKey } from '../models/storage-key.enum';
import { AuthStorageService } from './auth-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthStoreService {
  private userLogged = new BehaviorSubject<boolean>(false);
  private userClaims = new BehaviorSubject<string[]>(null);
  private userOrganizationId = new BehaviorSubject<string>(null);
  private userId = new BehaviorSubject<string | null>(null);
  private userFullName = new BehaviorSubject<string>(null);

  get getUserClaims(): string[] {
    if (this.userClaims && this.userClaims.value != null) {
      return this.userClaims.value;
    }

    return [];
  }

  get getUserOrganizationId(): string {
    if (this.userOrganizationId && this.userOrganizationId.value != null) {
      return this.userOrganizationId.value;
    }

    return;
  }

  get getUserId(): string | null {
    return this.userId.getValue();
  }

  get isAuthenticated(): boolean {
    return this.userLogged.value;
  }

  get getUserFullName(): string {
    if (this.userFullName && this.userFullName.value != null) {
      return this.userFullName.value;
    }

    throw new Error('Unable to get user full name.');
  }

  get getAccessToken(): string {
    return this.storageService.getItemSync<string>(StorageKey.ACCESS_TOKEN_KEY);
  }

  get getRefreshToken(): string {
    return this.storageService.getItemSync<string>(StorageKey.REFRESH_TOKEN_KEY);
  }

  constructor(private storageService: AuthStorageService) {
    this.initUserState();
  }

  private initUserState() {
    forkJoin({
      accessToken: this.storageService.getItem<string>(StorageKey.ACCESS_TOKEN_KEY),
      userProfile: this.storageService.getItem<UserProfileDto>(StorageKey.USER_PROFILE)
    }).subscribe(
      res => {
        const tokenIsValid = res.accessToken !== null && res.accessToken !== undefined && res.accessToken !== '';
        const userProfileExists = res.userProfile !== null;
        if (tokenIsValid && userProfileExists) {
          this.userLogged.next(true);
          this.updateUserDataInternal(res.userProfile);
        }
      },
      () => {
        this.updateUserNotLogged();
      }
    );
  }

  public updateAuthTokens(accessToken: string, refreshToken: string): Observable<void> {
    const accessTokenObs = this.storageService.setItem(StorageKey.ACCESS_TOKEN_KEY, accessToken);
    const refreshTokenObs = this.storageService.setItem(StorageKey.REFRESH_TOKEN_KEY, refreshToken);
    return forkJoin([accessTokenObs, refreshTokenObs]).pipe(
      tap(() => this.userLogged.next(accessToken !== null && refreshToken !== null)),
      switchMap(() => of(null))
    );
  }

  public setAccessToken(accessToken: string) {
    this.storageService.setItemSync(StorageKey.ACCESS_TOKEN_KEY, accessToken);
  }

  public setRefreshToken(refreshToken: string) {
    this.storageService.setItemSync(StorageKey.REFRESH_TOKEN_KEY, refreshToken);
  }

  public updateUserData(userProfile: UserProfileDto): Observable<void> {
    // For one client, we have to create irrigation functionality. URL is bound to his system, thus it need to be displayed only for his account.
    // That's the temporary solution and eventually will be deleted from code
    if (userProfile && userProfile.organizationId === '7808672b-58dd-406d-a5eb-c30ac6e07ed1') {
      userProfile.permissions.push('Irrigation.Temp');
    }

    return this.storageService.setItem(StorageKey.USER_PROFILE, userProfile).pipe(
      tap(() => {
        this.updateUserDataInternal(userProfile);
      }),
      map(() => null)
    );
  }

  private updateUserDataInternal(userProfile: UserProfileDto): void {
    this.userClaims.next(userProfile.permissions);
    this.userOrganizationId.next(userProfile.organizationId);
    this.userId.next(userProfile.userId);
    this.userFullName.next(userProfile.fullName);
  }

  public updateUserNotLogged(): void {
    this.updateAuthTokens(null, null);
    this.updateUserData(null);
  }
}
